import axios from "axios";
import moment from "moment";
import * as turf from "@turf/turf";

const startTime = moment().subtract(7, "days").toISOString(true).slice(0, 10);
const endTime = moment().toISOString(true).slice(0, 10);

type urlAndTimes = {
  url: string;
  creationTime: Date;
  creationRawDate: string;
};

const getGeoJSONUrlItaly = async (url: string): Promise<urlAndTimes> => {
  let axiosResponse;
  try {
    axiosResponse = await axios.get(url);
  } catch (error) {
    console.error(
      `Error fetching GeoJSON URL for Italy from ${url}:`,
      error,
      " This service can be down for maintenance.",
    );
    return {
      url: "cannot-fetch-URL",
      creationTime: new Date(1900, 0, 1),
      creationRawDate: "19000101",
    };
  }

  if (!axiosResponse.data || !axiosResponse.data.items || axiosResponse.data.items.length === 0) {
    console.error("Invalid response structure, NASA does not provide any GeoJSON URLs:", axiosResponse.data);
    return {
      url: "cannot-fetch-URL",
      creationTime: new Date(1900, 0, 1),
      creationRawDate: "19000101",
    };
  }

  const {
    data: {
      items: [{ properties, action }],
    },
  } = axiosResponse;

  console.log(action);

  if (action && action.length > 0) {
    const { using } = action.find((x: any) => x.displayName === "subset");
    if (using && using.length > 0) {
      const { url } = using.find((x: any) => x.displayName === "subset");
      // Replace "{LLlon},{LLlat},{URLon},{URLat}" by "6,35,19,47"
      // These are min/max lat/long values for Italy
      const urlForItaly = url.replace("{LLlon},{LLlat},{URLon},{URLat}", "6,35,19,47");
      const ret = {
        url: urlForItaly,
        creationTime: new Date(properties.date["@value"]), // ok for landslide
        creationRawDate: properties.date["@value"], // ok for rainfall
      };

      return ret;
    } else {
      console.error("No subset URL found");
    }
  } else {
    console.error("No action found");
  }
  return {
    url: "cannot-use-URL",
    creationTime: new Date(1900, 0, 1),
    creationRawDate: "19000101",
  };
};

// eslint-disable-next-line no-unused-vars
const getGeoJSONUrl = async (url: string) => {
  // This variant is not optimized for Italy
  const {
    data: {
      items: [{ action }],
    },
  } = await axios.get(url);
  if (action && action.length > 0) {
    const { using } = action.find((x: any) => x.displayName === "export");
    if (using && using.length > 0) {
      const { url } = using.find((x: any) => x.displayName === "geojson");
      return url;
    }
  }
};

export async function getRainfallGeoJSONUrl(longitude: number, latitude: number, days: number = 1) {
  let rainfallUrl = `https://pmmpublisher.pps.eosdis.nasa.gov/opensearch?q=precip_${days}d&lat=${latitude}&lon=${longitude}&limit=1&startTime=${startTime}&endTime=${endTime}`;
  const urlAndTime = await getGeoJSONUrlItaly(rainfallUrl);
  return urlAndTime;
}

export async function getRainfallOneDayGeoJSONUrlForItaly() {
  const urlAndTime = await getRainfallGeoJSONUrl(13.0, 42.0, 1);
  const url = urlAndTime.url;
  return url;
}
export async function getRainfallSevenDayGeoJSONUrlForItaly() {
  const url = (await getRainfallGeoJSONUrl(13.0, 42.0, 7)).url;
  return url;
}

export async function getGlobalLandslideNowcastGeoJsonUrlForItaly2(longitude: number, latitude: number) {
  const yesterday = moment().subtract(1, "day").utc().format("YYYY-MM-DD");
  const today = moment().utc().format("YYYY-MM-DD");

  const landslideUrl = `https://pmmpublisher.pps.eosdis.nasa.gov/opensearch?q=global_landslide_nowcast_3hr&lat=${latitude}&lon=${longitude}&limit=1&startTime=${yesterday}&endTime=${today}`;
  const urlAndTime = await getGeoJSONUrlItaly(landslideUrl);

  return urlAndTime;
}

export async function getGlobalLandslideNowcastGeoJsonUrlForItaly(): Promise<string> {
  const url = (await getGlobalLandslideNowcastGeoJsonUrlForItaly2(0, 0)).url;
  console.log("Global Landslide Nowcast GeoJSON URL for Italy:", url);
  return url;
}

export async function getGlobalLandslideNowcastCreationTime(): Promise<Date> {
  return (await getGlobalLandslideNowcastGeoJsonUrlForItaly2(0, 0)).creationTime;
}

export async function getRainFallOneDayCreationTime(): Promise<string> {
  return (await getRainfallGeoJSONUrl(13.0, 42.0, 1)).creationRawDate;
}
export async function getRainFallSevenDayCreationTime(): Promise<string> {
  return (await getRainfallGeoJSONUrl(13.0, 42.0, 7)).creationRawDate;
}

export async function getLandslideValue(lat: number, lng: number): Promise<number | "NaN"> {
  const url: string = (await getGlobalLandslideNowcastGeoJsonUrlForItaly2(lng, lat)).url;

  let response;
  try {
    response = await axios.get(url);
  } catch (error) {
    console.error(`Error fetching rainfall data: ${error}`);
    return "NaN";
  }
  const point = turf.point([lng, lat]);
  let value: number = 0;
  response.data.features.forEach((feature: any) => {
    const isWithIn = turf.booleanWithin(point, feature);
    if (isWithIn) {
      value = feature.properties.nowcast;
    }
  });

  return value;
}

export async function getRainfallXDayValue(lat: number, lng: number, days: number): Promise<number | "NaN"> {
  const urlAndDate: urlAndTimes = await getRainfallGeoJSONUrl(lng, lat, days);
  const url = urlAndDate.url;
  let response;
  try {
    response = await axios.get(url);
  } catch (error) {
    console.error(`Error fetching rainfall data: ${error}`);
    return "NaN";
  }

  const point = turf.point([lng, lat]);
  let value = 0;
  response.data.features.forEach((feature: any) => {
    const isWithIn = turf.booleanWithin(point, feature);
    if (isWithIn) {
      value = feature.properties.precip;
    }
  });

  return value;
}

export function getLandslideRatingAndLevel(landslideValue: number | "NaN") {
  if (landslideValue === "NaN") {
    return { level: "notAvailable", rating: 0 };
  } else if (landslideValue === 0) {
    return { level: "noHazard", rating: 0 };
  } else if (landslideValue === 1) {
    return { level: "moderate", rating: 1 };
  } else return { level: "high", rating: 2 };
}

export function getRainfallRatingAndLevel(rainfallValue: number | "NaN") {
  if (rainfallValue === "NaN") {
    return { level: "notAvailable", rating: 0 };
  }
  const thresholds = [0, 1, 2, 3, 5, 10, 20, 40, 70, 120, 200, 350, 600];
  const names = [
    "noHazard",
    "low",
    "low",
    "low",
    "low",
    "medium",
    "medium",
    "medium",
    "high",
    "high",
    "high",
    "high",
    "veryHigh",
  ];
  for (let i = 0; i < thresholds.length; i++) {
    if (rainfallValue <= thresholds[i]) {
      return { level: names[i], rating: i };
    }
  }
  return { level: "veryHigh", rating: 12 };
}
